/**
 * 服务端审核逻辑（默认轮询模式）
 */
import {
  AUDIT_POLL_FALLBACK_MS,
  AUDIT_MIN_POLL_COUNT,
  POLL_INTERVAL_CONFIG,
  AUDIT_STATUS,
  DEFAULT_AUDIT_BASE_URL,
  PRODUCTION_AUDIT_BASE_URL
} from '../utils/constants';
import { abortControllers } from '../utils/fetch';
import { createAuditClient } from '../utils/auditClient';

/**
 * 判断是否为审核目标图片
 * @param {*} image - 图片对象
 * @returns {boolean}
 */
function isAuditTarget(image) {
  return Boolean(image && typeof image === 'object' && image.im);
}

/**
 * 解析统一的源图片列表（阶段一加载与阶段三审核共用）
 * 优先使用 storeImages，按 image.im 去重
 * @param {Array} storeImages - store 中的图片数组
 * @param {Array} optionImages - options 中的图片数组
 * @returns {Array} 去重后的原始图片对象数组
 */
function resolveSourceImages(storeImages, optionImages) {
  let candidates = [];

  if (Array.isArray(storeImages) && storeImages.length) {
    candidates = storeImages;
  } else if (Array.isArray(optionImages)) {
    candidates = optionImages;
  }

  const seen = new Set();
  const sourceImages = [];

  candidates.forEach((image) => {
    if (!isAuditTarget(image)) {
      return;
    }

    const imageUrl = image.im;
    if (!imageUrl || seen.has(imageUrl)) {
      return;
    }

    seen.add(imageUrl);
    sourceImages.push(image);
  });

  return sourceImages;
}

/**
 * 根据源图片列表构建审核图片列表
 * @param {Array} sourceImages - resolveSourceImages 返回的源图片数组
 * @returns {Array} 审核图片列表 [{ index, url }]
 */
function buildAuditImagesFromSource(sourceImages) {
  if (!Array.isArray(sourceImages)) {
    return [];
  }

  return sourceImages.map((image, index) => ({
    index,
    url: image.im
  }));
}

/**
 * 根据图片数量获取轮询间隔配置
 * @param {number} imageCount - 图片数量
 * @returns {object} 轮询配置 { initial, min, max }
 */
function getPollIntervalConfig(imageCount) {
  if (imageCount <= POLL_INTERVAL_CONFIG.small.maxCount) {
    return POLL_INTERVAL_CONFIG.small;
  }
  if (imageCount <= POLL_INTERVAL_CONFIG.medium.maxCount) {
    return POLL_INTERVAL_CONFIG.medium;
  }
  if (imageCount <= POLL_INTERVAL_CONFIG.large.maxCount) {
    return POLL_INTERVAL_CONFIG.large;
  }
  return POLL_INTERVAL_CONFIG.xlarge;
}

/**
 * 计算当前轮询间隔（带轻微的指数退避）
 * @param {number} imageCount - 图片数量
 * @param {number} pollCount - 已轮询次数
 * @returns {number} 轮询间隔（毫秒）
 */
function calculatePollInterval(imageCount, pollCount) {
  const config = getPollIntervalConfig(imageCount);

  // 轻微的指数退避，但不超过最大间隔
  const backoffFactor = Math.min(1 + pollCount * 0.1, 2);
  const interval = Math.min(config.initial * backoffFactor, config.max);

  return Math.max(interval, config.min);
}

function normalizeAuditBaseUrlMode(mode) {
  if (!mode) {
    return 'auto';
  }

  const normalizedMode = String(mode).toLowerCase();
  if (['auto', 'debug', 'prod', 'custom'].includes(normalizedMode)) {
    return normalizedMode;
  }

  return 'auto';
}

function isDebugEnvironment(utilConfig) {
  if (!utilConfig || utilConfig.ISDEBUG === undefined || utilConfig.ISDEBUG === null) {
    return true;
  }

  const rawValue = utilConfig.ISDEBUG;

  if (rawValue === true || rawValue === 1 || rawValue === '1') {
    return true;
  }

  if (rawValue === false || rawValue === 0 || rawValue === '0') {
    return false;
  }

  return Boolean(rawValue);
}

/**
 * 规范化任务 payload
 * @param {object} responseData - 响应数据（裸对象，无 success/data 包裹）
 * @param {number} auditTotalCount - 审核图片总数
 * @returns {object} 规范化后的 payload
 */
function normalizeJobPayload(responseData, auditTotalCount) {
  const data = responseData || {};

  return {
    jobId: data.jobId || '',
    albumKey: data.albumKey || '',
    status: data.status || AUDIT_STATUS.QUEUED,
    totalCount: typeof data.totalCount === 'number' ? data.totalCount : auditTotalCount,
    auditedCount: typeof data.auditedCount === 'number' ? data.auditedCount : 0,
    passCount: typeof data.passCount === 'number' ? data.passCount : 0,
    pollAfterMs: typeof data.pollAfterMs === 'number' ? data.pollAfterMs : AUDIT_POLL_FALLBACK_MS,
    rejectedImageUrl: data.rejectedImageUrl || '',
    rejectedImageIndex: typeof data.rejectedImageIndex === 'number' ? data.rejectedImageIndex : -1,
    rejectedRiskLevel: data.rejectedRiskLevel || '',
    rejectedLabel: data.rejectedLabel || '',
    errorCode: data.errorCode || data.error || '',
    errorMessage: data.errorMessage || data.message || ''
  };
}

export function useAudit() {
  const requestControllers = [];
  let pollTimer = null;

  return {
    /**
     * 解析统一的源图片列表（阶段一加载与阶段三审核共用）
     * @param {Array} storeImages - store 中的图片数组
     * @param {Array} optionImages - options 中的图片数组
     * @returns {Array} 去重后的原始图片对象数组
     */
    resolveSourceImages,

    /**
     * 根据源图片列表构建审核图片列表
     * @param {Array} sourceImages - resolveSourceImages 返回的源图片数组
     * @returns {Array} 审核图片列表 [{ index, url }]
     */
    buildAuditImagesFromSource,

    /**
     * 构建审核图片列表（去重）
     * @param {Array} storeImages - store 中的图片数组
     * @param {Array} optionImages - options 中的图片数组
     * @returns {Array} 去重后的图片列表 [{ index, url }]
     */
    buildAuditImages(storeImages, optionImages) {
      return buildAuditImagesFromSource(resolveSourceImages(storeImages, optionImages));
    },

    /**
     * 解析审核服务地址
     * @param {string} optionAuditBaseUrl - options 中配置的地址
     * @param {string} optionAuditBaseUrlMode - options 中配置的域名模式
     * @param {object} utilConfig - $util.$config
     * @param {object} storeConfig - $store.state.config
     * @returns {string} 审核服务地址
     */
    resolveAuditBaseUrl(optionAuditBaseUrl, optionAuditBaseUrlMode, utilConfig, storeConfig) {
      const manualBaseUrl = optionAuditBaseUrl
        || (utilConfig && utilConfig.MAINLOADING_AUDIT_BASE_URL)
        || (storeConfig && storeConfig.mainloading_audit_base_url)
        || (typeof window !== 'undefined' && window.__TD_SHARE_MAINLOADING_AUDIT_BASE_URL__);
      const modeFromConfig = optionAuditBaseUrlMode
        || (utilConfig && utilConfig.MAINLOADING_AUDIT_BASE_URL_MODE)
        || (storeConfig && storeConfig.mainloading_audit_base_url_mode)
        || (typeof window !== 'undefined' && window.__TD_SHARE_MAINLOADING_AUDIT_BASE_URL_MODE__);
      const resolvedMode = normalizeAuditBaseUrlMode(modeFromConfig);
      const isDebugEnv = isDebugEnvironment(utilConfig);

      // 线上环境强制固定生产域名，忽略任何手动切换
      if (!isDebugEnv) {
        return PRODUCTION_AUDIT_BASE_URL;
      }

      if (resolvedMode === 'debug') {
        return DEFAULT_AUDIT_BASE_URL;
      }

      if (resolvedMode === 'prod') {
        return PRODUCTION_AUDIT_BASE_URL;
      }

      if (resolvedMode === 'custom' && manualBaseUrl) {
        return String(manualBaseUrl).replace(/\/$/, '');
      }

      // 兼容旧逻辑：未声明 mode 时，仍允许直接传完整域名覆盖
      if (!modeFromConfig && manualBaseUrl) {
        return String(manualBaseUrl).replace(/\/$/, '');
      }

      return DEFAULT_AUDIT_BASE_URL;
    },

    /**
     * 获取相册 key
     * @param {object} routerParams - $util.$router.params
     * @param {object} storeStateData - $store.state.data
     * @returns {string} 相册 key
     */
    getAlbumKey(routerParams, storeStateData) {
      if (routerParams && routerParams.key) {
        return routerParams.key;
      }

      if (storeStateData && storeStateData.key) {
        return storeStateData.key;
      }

      return '';
    },

    /**
     * 批量取消请求
     * @param {string} reason - 取消原因
     */
    abortAll(reason = 'aborted') {
      abortControllers(requestControllers, reason);
    },

    /**
     * 清除轮询定时器
     */
    clearPollTimer() {
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
    },

    /**
     * 等待下一次轮询
     * @param {number} delay - 延迟时间
     * @returns {Promise<void>}
     */
    waitForNextPoll(delay) {
      return new Promise(resolve => {
        this.clearPollTimer();
        pollTimer = setTimeout(() => {
          pollTimer = null;
          resolve();
        }, delay);
      });
    },

    /**
     * 开始服务端审核（默认轮询模式）
     * @param {object} options - 选项
     * @param {string} options.auditBaseUrl - 审核服务地址
     * @param {string} options.albumKey - 相册 key
     * @param {Array} options.auditImages - 审核图片列表
     * @param {Function} options.onStatusChange - 状态文本变化回调 (text, finishedCount, totalCount) => void
     * @param {Function} options.onProgress - 进度更新回调 (finishedCount, totalCount) => void
     * @param {Function} options.onTimeout - 轮询上限回调，返回 Promise<'retry' | 'give_up'>
     * @param {Function} options.onMonitor - 监控上报回调 (payload) => void
     * @param {Function} options.isDebugLogEnabled - 是否启用调试日志的函数
     * @param {object} tokenRef - 用于取消审核的 token 引用 { current: number }
     * @param {Function} isTokenValid - 判断 token 是否有效的函数 (currentToken) => boolean
     * @returns {Promise<void>}
     */
    async startServerAudit(
      {
        auditBaseUrl,
        albumKey,
        auditImages,
        preheatResults,
        clientDomain,
        onStatusChange,
        onProgress,
        onTimeout,
        onMonitor,
        isDebugLogEnabled,
        debugScenario
      },
      tokenRef,
      isTokenValid
    ) {
      const auditToken = Date.now();
      tokenRef.current = auditToken;

      const logDebug = (eventName, payload) => {
        if (typeof isDebugLogEnabled === 'function' && isDebugLogEnabled()) {
          console.log('[MainloadingServe][audit]', eventName, payload || {});
        }
      };

      const canContinue = () => isTokenValid(auditToken);

      if (!auditBaseUrl) {
        throw new Error('未配置服务端审核地址');
      }

      const auditTotalCount = auditImages.length;
      const auditClient = createAuditClient({
        auditBaseUrl,
        albumKey,
        requestControllers,
        debugScenario,
        auditTotalCount,
        preheatResults,
        clientDomain
      });

      // 上报审核开始
      if (typeof onMonitor === 'function') {
        onMonitor({
          monitorType: 'event',
          eventName: 'mainloading_audit_request_start',
          message: 'start mainloading audit request',
          extra: {
            auditImageCount: auditTotalCount
          }
        });
      }

      // 更新状态：准备审核
      if (typeof onStatusChange === 'function') {
        onStatusChange('加载中', 0, auditTotalCount);
      }

      // 创建审核任务
      const createResponse = await auditClient.createJob();

      if (!canContinue()) {
        return;
      }

      let jobPayload = normalizeJobPayload(createResponse, auditTotalCount);
      logDebug('job_created', jobPayload);

      // 快速路径：服务端优化，直接返回终态（不依赖 waitForResult）
      if (jobPayload.status === AUDIT_STATUS.PASSED) {
        if (typeof onProgress === 'function') {
          onProgress(auditTotalCount, auditTotalCount);
        }
        if (typeof onMonitor === 'function') {
          onMonitor({
            monitorType: 'event',
            eventName: 'mainloading_audit_passed',
            auditStatus: AUDIT_STATUS.PASSED,
            message: 'mainloading audit passed',
            extra: {
              totalCount: jobPayload.totalCount,
              auditedCount: jobPayload.auditedCount
            }
          });
        }
        return;
      }

      if (jobPayload.status === AUDIT_STATUS.REJECTED) {
        const rejectedError = new Error('audit_rejected');
        rejectedError.code = 'audit_rejected';
        rejectedError.payload = jobPayload;
        throw rejectedError;
      }

      if (jobPayload.status === AUDIT_STATUS.FAILED) {
        const failedError = new Error(jobPayload.errorMessage || 'audit_job_failed');
        failedError.code = jobPayload.errorCode || 'audit_job_failed';
        failedError.payload = jobPayload;
        throw failedError;
      }

      if (jobPayload.status === AUDIT_STATUS.TIMEOUT) {
        const timeoutError = new Error(jobPayload.errorMessage || 'audit_job_timeout');
        timeoutError.code = jobPayload.errorCode || AUDIT_STATUS.TIMEOUT;
        timeoutError.payload = jobPayload;
        throw timeoutError;
      }

      // 必须有 jobId 才能轮询
      if (!jobPayload.jobId) {
        throw new Error('missing_job_id');
      }

      // 开始轮询
      let pollCount = 0;
      let maxPollCount = auditTotalCount;
      if (typeof jobPayload.totalCount === 'number' && jobPayload.totalCount > 0) {
        maxPollCount = jobPayload.totalCount;
      }
      maxPollCount = Math.max(Math.ceil(maxPollCount || 0), AUDIT_MIN_POLL_COUNT);

      while (true) {
        if (!canContinue()) {
          return;
        }

        // 检查是否达到最大轮询次数
        if (pollCount >= maxPollCount) {
          logDebug('audit_poll_limit_reached', { pollCount, maxPollCount });

          // 触发上限提示，等待用户决定
          if (typeof onTimeout === 'function') {
            const action = await onTimeout({
              pollCount,
              maxPollCount
            });

            if (action === 'retry') {
              pollCount = 0;
            } else {
              const giveUpError = new Error('audit_poll_limit_give_up');
              giveUpError.code = 'audit_poll_limit_give_up';
              throw giveUpError;
            }
          }
        }

        // 轮询任务状态
        const statusResponse = await auditClient.getJob(jobPayload.jobId);

        jobPayload = normalizeJobPayload(statusResponse, auditTotalCount);
        logDebug('poll_result', jobPayload);

        if (!canContinue()) {
          return;
        }

        // 更新进度
        const currentAuditedCount = jobPayload.auditedCount > auditTotalCount ? auditTotalCount : jobPayload.auditedCount;
        if (typeof onProgress === 'function') {
          onProgress(currentAuditedCount, auditTotalCount);
        }

        // 更新状态文本
        if (typeof onStatusChange === 'function') {
          if (jobPayload.status === AUDIT_STATUS.QUEUED) {
            onStatusChange('正在内容安全校验', currentAuditedCount, auditTotalCount);
          } else if (jobPayload.status === AUDIT_STATUS.RUNNING) {
            onStatusChange('安全检测中', currentAuditedCount, auditTotalCount);
          }
        }

        // 检查终态
        if (jobPayload.status === AUDIT_STATUS.PASSED) {
          if (typeof onMonitor === 'function') {
            onMonitor({
              monitorType: 'event',
              eventName: 'mainloading_audit_passed',
              auditStatus: AUDIT_STATUS.PASSED,
              message: 'mainloading audit passed',
              extra: {
                totalCount: jobPayload.totalCount,
                auditedCount: jobPayload.auditedCount
              }
            });
          }
          break;
        }

        if (jobPayload.status === AUDIT_STATUS.REJECTED) {
          const rejectedError = new Error('audit_rejected');
          rejectedError.code = 'audit_rejected';
          rejectedError.payload = jobPayload;
          throw rejectedError;
        }

        if (jobPayload.status === AUDIT_STATUS.FAILED) {
          const failedError = new Error(jobPayload.errorMessage || 'audit_job_failed');
          failedError.code = jobPayload.errorCode || 'audit_job_failed';
          failedError.payload = jobPayload;
          throw failedError;
        }

        if (jobPayload.status === AUDIT_STATUS.TIMEOUT) {
          const timeoutError = new Error(jobPayload.errorMessage || 'audit_job_timeout');
          timeoutError.code = jobPayload.errorCode || AUDIT_STATUS.TIMEOUT;
          timeoutError.payload = jobPayload;
          throw timeoutError;
        }

        // 计算下一次轮询间隔
        pollCount++;
        const pollAfterMs = jobPayload.pollAfterMs > 0 ? jobPayload.pollAfterMs : calculatePollInterval(auditTotalCount, pollCount);
        logDebug('wait_next_poll', { pollAfterMs, pollCount });

        await this.waitForNextPoll(pollAfterMs);
      }
    }
  };
}
